import { useId, type ReactElement } from 'react';
import { Link } from '../components/Link';

/**
 * The homepage's sign to the Chats list (2026-10-21 founder): a wooden arrow
 * on a post, planted on the ground left of the trunk (BanyanScene signpost),
 * for everyone signed in — with or without a room. While something is unread
 * across the conversations a small lamp-coloured dot hangs on the arrow's
 * corner; no number (the top bar's Chats door carries the count).
 *
 * Meet people (2026-10-26 founder, named so 2026-10-27): for a person who may
 * meet people (GET /api/me/chats `friends.eligible`), a second board on the
 * same post, under Chats and pointing the other way, to the Meet people page —
 * there by default, not only once something is under way — with a ? on it
 * that opens the intro (`onHelp`). A person who may not sees the post as it was.
 *
 * The ? (2026-10-28 founder) is a small ember-coloured dot on the board's
 * top-right corner, half over the edge, swaying with the board — not wood, not
 * floating. "new!" (`fresh`) is written above the board's point while the edge
 * says so (friends.homeSign). `reveal`: the intro has just folded back into the
 * sign — the board glows once and both come out.
 */
export function ChatsSign({ lit, friends = false, onHelp, fresh = false, reveal = false }: {
  lit: boolean; friends?: boolean; onHelp?: () => void; fresh?: boolean; reveal?: boolean;
}): ReactElement {
  const glow = `${useId().replace(/:/g, '')}-glow`;
  const chatsBoard = (
    <>
      <path className="chats-sign-board" d="M7 12 H80 L93 26 L80 40 H7 Q4 40 4 37 V15 Q4 12 7 12 Z" strokeWidth="1.6" />
      {/* the light on its top edge, as on the far bank's ridge */}
      <path className="chats-sign-hi" d="M7 13.2 H79.5 L85 19" strokeWidth="1.4" strokeLinecap="round" opacity=".75" />
      <path className="chats-sign-grain" d="M12 22 H38 M54 33 H74" strokeWidth="1.2" strokeLinecap="round" />
      <circle className="chats-sign-edge" cx="10" cy="26" r="1.6" />
      <circle className="chats-sign-edge" cx="76" cy="26" r="1.6" />
      <text className="chats-sign-word" x="43" y="31.5" textAnchor="middle">Chats</text>
      {lit ? (
        <>
          <defs>
            <radialGradient id={glow}>
              <stop offset="0" stopColor="#ffd58a" stopOpacity=".85" />
              <stop offset="1" stopColor="#f6b352" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx="86" cy="11" r="13" fill={`url(#${glow})`} />
          <circle className="chats-sign-lamp" cx="86" cy="11" r="5" strokeWidth="1.4" />
        </>
      ) : null}
    </>
  );
  const chatsLabel = lit ? 'Chats, new messages' : 'Chats';
  if (!friends) {
    return (
      <Link to="/chats" className="chats-sign" aria-label={chatsLabel} data-log="chats_entry" data-log-state="sign">
        <svg viewBox="0 0 100 84" width="100" height="84" aria-hidden>
          <ellipse className="chats-sign-shadow" cx="50" cy="81" rx="16" ry="3" />
          <rect className="chats-sign-wood" x="46" y="34" width="8" height="48" rx="1.5" />
          <rect className="chats-sign-wood-hi" x="52" y="34" width="2" height="48" opacity=".6" />
          {chatsBoard}
        </svg>
      </Link>
    );
  }
  // The post stands at the middle of the 140-wide sign (BanyanScene centres the signpost on it): Chats on top, as
  // before; Meet people under it, pointing left, swaying a little.
  return (
    <div className={`chats-sign-post${reveal ? ' is-reveal' : ''}`}>
      {reveal ? <span className="friends-sign-glow" aria-hidden /> : null}
      <svg className="chats-sign-pole" viewBox="0 0 140 132" width="140" height="132" aria-hidden>
        <ellipse className="chats-sign-shadow" cx="70" cy="129" rx="17" ry="3" />
        <rect className="chats-sign-wood" x="66" y="34" width="8" height="96" rx="1.5" />
        <rect className="chats-sign-wood-hi" x="72" y="34" width="2" height="96" opacity=".6" />
      </svg>
      <Link to="/chats" className="chats-sign chats-sign-top" aria-label={chatsLabel} data-log="chats_entry" data-log-state="sign">
        <svg viewBox="0 0 100 46" width="100" height="46" aria-hidden>{chatsBoard}</svg>
      </Link>
      {fresh ? (
        <span className="friends-sign-new" aria-hidden>
          new!
          <svg width="11" height="11" viewBox="0 0 12 12"><path d="M6 0 7.4 4.6 12 6 7.4 7.4 6 12 4.6 7.4 0 6 4.6 4.6Z" fill="#f6b352" /></svg>
        </span>
      ) : null}
      {/* The board and its ? sway together. */}
      <div className="friends-sign-board">
        <Link to="/friends" className="chats-sign friends-sign" aria-label={fresh ? 'Meet people, new' : 'Meet people'} data-log="friends_entry" data-log-state="sign">
          <svg viewBox="0 0 136 34" width="136" height="34" aria-hidden>
            <path className="chats-sign-board" d="M20 2 H130 Q133 2 133 5 V29 Q133 32 130 32 H20 L5 17 Z" strokeWidth="1.6" />
            <path className="chats-sign-hi" d="M20.5 3.2 H129" strokeWidth="1.4" strokeLinecap="round" opacity=".75" />
            <path className="chats-sign-grain" d="M30 26 H52 M98 10 H124" strokeWidth="1.2" strokeLinecap="round" />
            <circle className="chats-sign-edge" cx="76" cy="9" r="1.6" />
            <text className="chats-sign-word friends-sign-word" x="72" y="22.5" textAnchor="middle">Meet people</text>
          </svg>
        </Link>
        {onHelp ? (
          <button type="button" className="friends-sign-help" aria-label="What is Meet people?" data-log="friends_help" data-log-state="sign" onClick={onHelp}>
            <span aria-hidden>?</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}
